import { create } from "zustand";

/**
 * The open document's name, for chrome that sits outside the editor.
 *
 * The shell bar shows and edits the document title, which it could read
 * straight from `editorStore` — and did, briefly. That import cost six test
 * files their 5-second budget: the shell bar is pulled in by almost
 * everything, `editorStore` is large, and every module that touched the shell
 * suddenly paid for the whole editor's graph. A suite that passes alone and
 * fails together is usually saying something about imports, not logic.
 *
 * So the shell reads this instead: two fields and a setter, with no
 * dependencies of its own. `EditorPage` keeps it in step and supplies the
 * rename callback, so renaming still flows through `editorStore.setTitle` —
 * which is what keeps the Title block in lockstep and marks the document
 * dirty. The shell never learns any of that.
 */
interface OpenDocumentState {
  /** Empty when no document is open, which is when the bar shows nothing. */
  title: string;
  /** Null unless the open surface can accept a rename. */
  onRename: ((title: string) => void) | null;
  /** Set when the reader may only view it: whose document it is, for the
   *  shell bar's "View only · shared by …" chip (view-only handoff). */
  sharedBy: { name: string; email?: string } | null;
  /** Bumped by "Rename" in the editor's ⋯: the shell bar focuses its title. */
  renameRequest: number;
  requestRename: () => void;

  setOpenDocument: (
    title: string,
    onRename: ((title: string) => void) | null,
    sharedBy?: { name: string; email?: string } | null,
  ) => void;
  clearOpenDocument: () => void;
}

export const useOpenDocumentStore = create<OpenDocumentState>()((set) => ({
  title: "",
  onRename: null,
  sharedBy: null,
  renameRequest: 0,
  requestRename: () => set((s) => ({ renameRequest: s.renameRequest + 1 })),

  setOpenDocument: (title, onRename, sharedBy = null) => set({ title, onRename, sharedBy }),
  clearOpenDocument: () => set({ title: "", onRename: null, sharedBy: null }),
}));
